import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { accountAPI } from '../../../api/client';
import { useTenant } from '../../../context/TenantContext';
import { setDisplay } from '../../../lib/displayFormat';
import { I18nProvider } from '../../../lib/i18n';

/**
 * The signed-in person's own settings for the Basic dashboard (Settings): loaded once, applied to the page as data
 * attributes (see preferences.css), and saved per section. Until they load, the theme remembered in this browser is used.
 */
export const THEME_KEY = 'tcx.dashboard.theme';
const remembered = () => { try { return localStorage.getItem(THEME_KEY) || 'light'; } catch { return 'light'; } };
const remember = (theme) => { try { localStorage.setItem(THEME_KEY, theme); } catch { /* only not remembered */ } };

export const DEFAULTS = {
  appearance: { theme: 'light', accent: 'green', font_size: 'medium', density: 'comfortable' },
  accessibility: { contrast: 'normal', reduce_motion: false, focus_indicators: false, cursor: 'normal', color_blind: 'none' },
  language: { language: 'en', date_format: null, time_format: null, first_day: 'monday', timezone: null, phone_format: 'national', currency: 'INR' },
  notifications: null,
  security: null,
};

const PreferencesContext = createContext(null);

function useSystemDark() {
  const query = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
  const [dark, setDark] = useState(() => !!query?.matches);
  useEffect(() => {
    if (!query) return undefined;
    const on = (e) => setDark(e.matches);
    query.addEventListener?.('change', on);
    return () => query.removeEventListener?.('change', on);
  }, [query]);
  return dark;
}

export function PreferencesProvider({ children }) {
  const { tenant } = useTenant();
  const [account, setAccount] = useState(null); // {preferences, avatar, two_factor}
  const [localTheme, setLocalTheme] = useState(remembered);
  const systemDark = useSystemDark();

  const reload = useCallback(() => accountAPI.get().then((res) => {
    setAccount(res.data);
    remember(res.data.preferences.appearance.theme);
    return res.data;
  }).catch(() => null), []);
  useEffect(() => { reload(); }, [reload]);

  const prefs = useMemo(() => account?.preferences || { ...DEFAULTS, appearance: { ...DEFAULTS.appearance, theme: localTheme } },
    [account, localTheme]);

  /** Saves one section (only the given values); the page changes straight away and goes back if saving fails. */
  const save = useCallback(async (section, values) => {
    const before = account;
    setAccount((a) => (a ? { ...a, preferences: { ...a.preferences, [section]: { ...a.preferences[section], ...values } } } : a));
    if (section === 'appearance' && values.theme) { setLocalTheme(values.theme); remember(values.theme); }
    try {
      const res = await accountAPI.savePreferences(section, values);
      setAccount((a) => ({ ...(a || {}), preferences: res.data.preferences }));
      return res.data.preferences;
    } catch (err) {
      setAccount(before);
      throw err;
    }
  }, [account]);

  const setAvatar = (avatar) => setAccount((a) => ({ ...(a || {}), avatar }));
  const setTwoFactor = (twoFactor) => setAccount((a) => ({ ...(a || {}), two_factor: twoFactor }));

  const theme = prefs.appearance.theme;
  const dark = theme === 'dark' || (theme === 'system' && systemDark);
  const lang = prefs.language.language;

  // Dates and times follow the person's choice, else the company's.
  useEffect(() => {
    setDisplay({
      date: prefs.language.date_format || tenant.date_format || 'DD MMM YYYY',
      time: prefs.language.time_format || tenant.time_format || '12',
      timeZone: prefs.language.timezone || tenant.timezone || undefined,
      phone: prefs.language.phone_format, currency: prefs.language.currency, firstDay: prefs.language.first_day,
    });
  }, [prefs.language, tenant.date_format, tenant.time_format, tenant.timezone]);

  useEffect(() => { document.documentElement.lang = lang; }, [lang]);

  const attrs = useMemo(() => ({
    'data-accent': prefs.appearance.accent, 'data-font': prefs.appearance.font_size, 'data-density': prefs.appearance.density,
    'data-contrast': prefs.accessibility.contrast, 'data-motion': prefs.accessibility.reduce_motion ? 'reduce' : 'full',
    'data-focus': prefs.accessibility.focus_indicators ? 'on' : 'off', 'data-cursor': prefs.accessibility.cursor,
    'data-cb': prefs.accessibility.color_blind,
  }), [prefs]);

  const value = useMemo(() => ({
    loaded: !!account, prefs, avatar: account?.avatar || null, twoFactor: account?.two_factor || null, dark, attrs,
    save, reload, setAvatar, setTwoFactor,
  }), [account, prefs, dark, attrs, save, reload]);

  return (
    <PreferencesContext.Provider value={value}>
      <I18nProvider lang={lang}>{children}</I18nProvider>
    </PreferencesContext.Provider>
  );
}

/** { loaded, prefs, avatar, twoFactor, dark, attrs, save(section, values), reload, setAvatar, setTwoFactor } */
export const usePreferences = () => useContext(PreferencesContext);
